import { useCallback } from "react";
import { toast } from "sonner";

/**
 * Saved-views CRUD bound to project.saved_views. The hook is intentionally
 * parameter-heavy so it can read and restore all relevant UI state without
 * pulling ProjectView into its own context shape.
 */
export default function useSavedViews({
  project,
  email,
  updateProject,
  // Read-current-state accessors
  currentState, // () => { search, workstream_filter, ... }
  // Apply-state setters
  applyState, // (view) => void — ProjectView decides how to hydrate filters/prefs
}) {
  const saveView = useCallback(
    async (name) => {
      const snapshot = currentState();
      const newView = {
        id: `view_${Date.now().toString(36)}`,
        name,
        created_by: email,
        created_at: new Date().toISOString(),
        ...snapshot,
      };
      const next = [...(project?.saved_views || []), newView];
      await updateProject({ saved_views: next });
      toast.success(`Saved view: ${name}`);
    },
    [project, email, updateProject, currentState],
  );

  const applyView = useCallback(
    (view) => {
      if (!view) return;
      applyState(view);
      toast.success(`Applied view: ${view.name}`);
    },
    [applyState],
  );

  const deleteView = useCallback(
    async (id) => {
      const next = (project?.saved_views || []).filter((v) => v.id !== id);
      await updateProject({ saved_views: next });
      toast.success("View deleted");
    },
    [project, updateProject],
  );

  return { saveView, applyView, deleteView };
}
